import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placeOverlay, type OverlayCoords, type OverlayPlacement } from './overlayPosition';

export type TooltipPlacement = OverlayPlacement;

interface TooltipProps {
    children: React.ReactNode;
    content: React.ReactNode;
    placement?: TooltipPlacement;
    className?: string;
    /** Keep the hint open. Used by Storybook. */
    forceOpen?: boolean;
    /**
     * Put the wrapper in the tab order so keyboard users can read the hint.
     * Turn this off when the child is already a button or link.
     */
    focusable?: boolean;
}

const sameCoords = (a: OverlayCoords | null, b: OverlayCoords) =>
    a != null && a.top === b.top && a.left === b.left && a.ax === b.ax && a.ay === b.ay;

export const Tooltip = ({
    children,
    content,
    placement = 'top',
    className = '',
    forceOpen = false,
    focusable = true,
}: TooltipProps) => {
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const isOpen = forceOpen || hovered || focused;
    const [coords, setCoords] = useState<OverlayCoords | null>(null);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const tooltipRef = useRef<HTMLSpanElement>(null);
    const tooltipId = useId();

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const tooltip = tooltipRef.current;
        if (!isOpen || !trigger || !tooltip) {
            setCoords(null);
            return;
        }
        const next = placeOverlay(placement, trigger.getBoundingClientRect(), tooltip.getBoundingClientRect());
        setCoords((current) => (sameCoords(current, next) ? current : next));
    }, [isOpen, placement]);

    useLayoutEffect(() => {
        updatePosition();
    }, [updatePosition, content]);

    React.useEffect(() => {
        if (!isOpen) return;
        const handleUpdate = () => updatePosition();
        window.addEventListener('resize', handleUpdate);
        window.addEventListener('scroll', handleUpdate, true);
        return () => {
            window.removeEventListener('resize', handleUpdate);
            window.removeEventListener('scroll', handleUpdate, true);
        };
    }, [isOpen, updatePosition]);

    const tooltipNode =
        isOpen && typeof document !== 'undefined'
            ? createPortal(
                  <span
                      ref={tooltipRef}
                      role="tooltip"
                      id={tooltipId}
                      className={`tooltip tooltip-${placement} is-show`}
                      style={
                          {
                              top: coords?.top ?? 0,
                              left: coords?.left ?? 0,
                              visibility: coords ? 'visible' : 'hidden',
                              '--ax': `${coords?.ax ?? 10}px`,
                              '--ay': `${coords?.ay ?? -5}px`,
                          } as React.CSSProperties
                      }
                  >
                      {content}
                  </span>,
                  document.body,
              )
            : null;

    return (
        <span
            className={['tooltip-trigger', className].filter(Boolean).join(' ')}
            ref={triggerRef}
            tabIndex={focusable ? 0 : undefined}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(event) => {
                if (event.key === 'Escape') {
                    setHovered(false);
                    setFocused(false);
                }
            }}
            aria-describedby={isOpen ? tooltipId : undefined}
        >
            {children}
            {tooltipNode}
        </span>
    );
};

export default Tooltip;
